import { Component, createRef, type RefObject, type ReactNode } from 'react';

import Tabs from './Tabs/Tabs';

export interface Props {
  tabItems: any[];
  children: ReactNode;
  analyticsOrigin: string;
}

interface State {
  tabIndex: number;
  slider: any;
}

class TabsContainer extends Component<Props, State> {
  private tabRefs: Array<RefObject<any>>;

  constructor(props: Props) {
    super(props);
    this.tabRefs = props.tabItems.map(() => createRef());
  }

  state = {
    tabIndex: 0,
    slider: { offset: 0, scale: 0 },
  };

  componentDidMount() {
    this.calculateSlider();
    window.addEventListener('resize', this.calculateSlider);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateSlider);
  }

  calculateSlider = () => {
    const { tabIndex } = this.state;
    const { offset, scale } = this.calculateSliderWithIndex(tabIndex);
    this.setState({ slider: { offset, scale } });
  };

  scrollTabItemToView = (index: number) => {
    const tabElement = this.tabRefs[index].current;

    if (!tabElement || !tabElement.scrollIntoViewIfNeeded) {
      return;
    }

    tabElement.scrollIntoViewIfNeeded(true);
  };

  calculateSliderWithIndex = (index: number) => {
    const tabElement = this.tabRefs[index].current;

    if (!tabElement) {
      return { offset: 0, scale: 0 };
    }

    const parentRect = tabElement.parentNode.getBoundingClientRect();
    const tabRect = tabElement.getBoundingClientRect();

    return {
      offset: tabRect.x - parentRect.x,
      scale: tabRect.width / parentRect.width,
    };
  };

  onTabClick = (event: Event, tabIndex: number) => {
    event.preventDefault();

    this.scrollTabItemToView(tabIndex);
    const { offset, scale } = this.calculateSliderWithIndex(tabIndex);
    this.setState({ tabIndex, slider: { offset, scale } });
  };

  render() {
    const { tabItems, analyticsOrigin, children } = this.props as Props;
    return (
      <Tabs
        {...this.state}
        tabItems={tabItems}
        analyticsOrigin={analyticsOrigin}
        tabRefs={this.tabRefs}
        onTabClick={this.onTabClick}
      >
        {children}
      </Tabs>
    );
  }
}

export default TabsContainer;
